import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Mail, Phone, Send, CheckCircle2, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    destinationInterest: '',
    preferredDates: '',
    budgetRange: '',
    additionalNotes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Inquiry Submitted!', {
          description: "Thank you! We'll contact you within 24 hours to start planning your dream vacation."
        });

        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          destinationInterest: '',
          preferredDates: '',
          budgetRange: '',
          additionalNotes: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Submission failed', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 mb-4">
            Get In Touch
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Start Planning Your Journey
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Let's create your perfect tropical escape together
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            {/* Email Card */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-[#ef571b] to-[#dc4f42] p-3 rounded-xl">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
                    <p className="text-gray-600 text-sm">info@vacaywithk.com</p>
                    <p className="text-gray-600 text-sm">bookings@vacaywithk.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-[#ef571b] to-[#dc4f42] p-3 rounded-xl">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Call Us</h3>
                    <p className="text-gray-600 text-sm">+1 (555) 123-4567</p>
                    <p className="text-gray-600 text-sm">Available Mon-Fri, 9AM-6PM EST</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust Badge */}
            <div className="bg-gradient-to-br from-[#ef571b] to-[#dc4f42] rounded-2xl p-6 text-white">
              <CheckCircle2 className="h-12 w-12 mb-4" />
              <h3 className="text-xl font-bold mb-2">Trusted by 50,000+</h3>
              <p className="text-white/90 text-sm">Join thousands of happy travelers who've made their dream vacations a reality with us.</p>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2 border-0 shadow-2xl bg-white">
            <CardHeader>
              <CardTitle className="text-2xl">Plan Your Dream Vacation</CardTitle>
              <p className="text-gray-600 text-sm mt-2">Fill out the form below and we'll create a personalized itinerary for you</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName" className="text-gray-700">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-700">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                    />
                  </div>
                </div>

                {/* Destination Interest */}
                <div>
                  <Label htmlFor="destinationInterest" className="text-gray-700">Destination or Travel Type *</Label>
                  <Input
                    id="destinationInterest"
                    name="destinationInterest"
                    value={formData.destinationInterest}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Maldives, Beach Vacation, Honeymoon Package"
                    className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                  />
                </div>

                {/* Preferred Dates & Budget */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="preferredDates" className="text-gray-700 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Preferred Travel Dates
                    </Label>
                    <Input
                      id="preferredDates"
                      name="preferredDates"
                      value={formData.preferredDates}
                      onChange={handleChange}
                      placeholder="e.g., June 2025, Summer 2025"
                      className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budgetRange" className="text-gray-700 flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Budget Range
                    </Label>
                    <Input
                      id="budgetRange"
                      name="budgetRange"
                      value={formData.budgetRange}
                      onChange={handleChange}
                      placeholder="e.g., $3,000-$5,000 per person"
                      className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                    />
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <Label htmlFor="additionalNotes" className="text-gray-700">Additional Notes or Special Requests</Label>
                  <Textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Tell us about your dream vacation, special occasions, interests, or any specific requirements..."
                    className="mt-2 border-gray-300 focus:border-[#ef571b] focus:ring-[#ef571b]"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#ef571b] hover:bg-[#d84915] text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      Submit Inquiry
                      <Send className="ml-2 h-5 w-5" />
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;